import React, { useState } from 'react';
import { C1SignupData } from '../C1SignupFlow';
import { usePartyDetailsForm, resolveEntityType, SECTOR_OPTIONS } from '../../../hooks/usePartyDetailsForm';
import { formatABN, formatACN } from '../../../utils/abnAcnValidator';
import { SessionRecoveryModal } from '../components';

interface SessionData {
  currentStep: string;
  email?: string;
  businessDetails?: Partial<C1SignupData>;
  reviewSign?: Partial<C1SignupData>;
  planSelection?: Partial<C1SignupData>;
  paymentDetails?: Partial<C1SignupData>;
}

interface StepProps {
  data: C1SignupData;
  onNext: (stepData: Partial<C1SignupData>) => void;
  onCancel: () => void;
  onSessionFoundByEmail?: (sessionData: SessionData) => void;
}

const PartyDetails: React.FC<StepProps> = ({ data, onNext, onCancel, onSessionFoundByEmail }) => {
  const {
    abnLookup,
    emailCheck,
    partyAddress, setPartyAddress,
    partyEmail, setPartyEmail,
    adminPhone, setAdminPhone,
    partySector, setPartySector,
    adminFirstName, setAdminFirstName,
    adminLastName, setAdminLastName,
    errors,
    handleSubmit,
  } = usePartyDetailsForm({
    initialData: {
      partyAbn: data.partyAbn,
      partyAddress: data.partyAddress,
      partyEmail: data.partyEmail,
      adminPhone: data.adminPhone,
      partySector: data.partySector,
      adminFirstName: data.adminFirstName,
      adminLastName: data.adminLastName,
    },
    onSessionFound: onSessionFoundByEmail as any,
  });

  const {
    abnAcnInput,
    inputLabel,
    lookupResult,
    isLookingUp,
    isRemoved,
    handleChange: handleAbnAcnChange,
    handleKeyDown,
    triggerLookup,
    digits,
  } = abnLookup;

  const [isProcessing, setIsProcessing] = useState(false);

  // ── Submit ────────────────────────────────────────────────
  const handleNext = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const result = await handleSubmit();
      if (result) {
        onNext(result);
      } else {
        setIsProcessing(false);
      }
    } catch {
      setIsProcessing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="step-form" data-testid="party-details-form">
      {/* ─── ABN / ACN ─── */}
      <div className="form-group">
        <label htmlFor="partyAbn">{inputLabel} *</label>
        <div className="abn-input-container" style={{ display: 'flex', gap: '8px' }}>
          <input
            id="partyAbn"
            type="text"
            data-testid="party-abn-input"
            placeholder="Enter ABN (11 digits) or ACN (9 digits)"
            value={abnAcnInput}
            onChange={handleAbnAcnChange}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn-secondary"
            data-testid="abn-lookup-btn"
            onClick={triggerLookup}
            disabled={isLookingUp || (!digits || (digits.length !== 11 && digits.length !== 9))}
            style={{ whiteSpace: 'nowrap', fontSize: '13px', minWidth: '110px' }}
          >
            {isLookingUp ? '⟳ Looking up…' : 'Lookup'}
          </button>
        </div>
        {errors.abnAcn && <div className="error" data-testid="party-abn-error">{errors.abnAcn}</div>}
        {!lookupResult && (
          <div className="abn-hint">
            <span>💡 Enter an ABN (11 digits) or ACN (9 digits) and press Enter or click Lookup</span>
          </div>
        )}

        {/* ─── Lookup Result Badge ─── */}
        {lookupResult && !isLookingUp && (
          <div className={`abn-result ${isRemoved ? 'warning' : 'success'}`} data-testid="abn-lookup-result" style={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span className="result-icon" style={{ fontSize: '1.2rem', marginTop: '2px' }}>{isRemoved ? '⚠' : '✓'}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontWeight: 600, fontSize: '1rem' }}>{lookupResult.name}</span>
                {lookupResult.abn && <span style={{ fontSize: '0.85rem', color: '#555' }}>ABN: {formatABN(lookupResult.abn.replace(/\D/g, ''))}</span>}
                {lookupResult.acn && <span style={{ fontSize: '0.85rem', color: '#555' }}>ACN: {formatACN(lookupResult.acn.replace(/\D/g, ''))}</span>}
                {'status' in lookupResult && lookupResult.status && lookupResult.status !== 'REMOVED' && (
                  <span style={{ fontSize: '0.85rem', color: '#555' }}>Status: {lookupResult.status}</span>
                )}
                {lookupResult.austracType && (
                  <span style={{ fontSize: '0.85rem', color: '#555' }}>AUSTRAC Type: {lookupResult.austracType}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Auto-filled Company Details (appear after lookup) ─── */}
      {lookupResult && !isLookingUp && (
        <>
          <div className="form-group">
            <label htmlFor="companyName">Company / Entity Name</label>
            <input
              id="companyName"
              type="text"
              data-testid="company-name-input"
              value={lookupResult.name || ''}
              readOnly
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#555' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="entityType">Entity Type</label>
            <input
              id="entityType"
              type="text"
              data-testid="entity-type-input"
              value={resolveEntityType(lookupResult.businessType)}
              readOnly
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#555' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="partyAddress">Business Address *</label>
            <input
              id="partyAddress"
              type="text"
              data-testid="party-address-input"
              placeholder="Business address"
              value={partyAddress}
              maxLength={60}
              onChange={(e) => setPartyAddress(e.target.value)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {errors.partyAddress
                ? <div className="error" data-testid="party-address-error">{errors.partyAddress}</div>
                : <span />}
              <span
                data-testid="party-address-char-count"
                style={{ fontSize: 11, color: partyAddress.length >= 60 ? '#DC2626' : '#94A3B8' }}
              >
                {partyAddress.length >= 60
                  ? 'Maximum 60 characters allowed'
                  : `${partyAddress.length}/60`}
              </span>
            </div>
          </div>
        </>
      )}

      {/* ─── Sector ─── */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label htmlFor="partySector">AUSTRAC Reporting Sector *</label>
        <select
          id="partySector"
          data-testid="party-sector-select"
          value={partySector}
          onChange={(e) => setPartySector(e.target.value)}
        >
          {SECTOR_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {errors.partySector && <div className="error" data-testid="party-sector-error">{errors.partySector}</div>}
      </div>

      {/* ─── Admin Contact ─── */}
      <fieldset style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
        <legend style={{ fontWeight: 600, fontSize: '0.95rem', padding: '0 8px' }}>Admin Contact Details</legend>

        <div style={{ display: 'flex', gap: '12px' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="adminFirstName">First Name *</label>
            <input
              id="adminFirstName"
              type="text"
              data-testid="admin-firstname-input"
              placeholder="Jane"
              value={adminFirstName}
              onChange={(e) => setAdminFirstName(e.target.value)}
            />
            {errors.adminFirstName && <div className="error" data-testid="admin-firstname-error">{errors.adminFirstName}</div>}
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="adminLastName">Last Name *</label>
            <input
              id="adminLastName"
              type="text"
              data-testid="admin-lastname-input"
              placeholder="Smith"
              value={adminLastName}
              onChange={(e) => setAdminLastName(e.target.value)}
            />
            {errors.adminLastName && <div className="error" data-testid="admin-lastname-error">{errors.adminLastName}</div>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="partyEmail">Email Address *</label>
          <div style={{ position: 'relative' }}>
            <input
              id="partyEmail"
              type="email"
              data-testid="party-email-input"
              placeholder="jane.smith@example.com"
              value={partyEmail}
              onChange={(e) => { setPartyEmail(e.target.value); emailCheck.clearError(); }}
              onBlur={() => emailCheck.checkEmail(partyEmail)}
            />
            {(emailCheck.isCheckingEmail || emailCheck.isCheckingSession) && (
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '0.8rem',
                color: '#666'
              }}>
                {emailCheck.isCheckingEmail ? 'Checking email...' : 'Checking...'}
              </span>
            )}
          </div>
          {errors.partyEmail && <div className="error" data-testid="party-email-error">{errors.partyEmail}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="adminPhone">Phone Number *</label>
          <input
            id="adminPhone"
            type="tel"
            data-testid="admin-phone-input"
            placeholder="+61 4XX XXX XXX"
            value={adminPhone}
            onChange={(e) => setAdminPhone(e.target.value)}
          />
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
            Include country code - e.g. +61 (Australia), +64 (New Zealand), +1 (USA), +44 (UK)
          </div>
          {errors.adminPhone && <div className="error">{errors.adminPhone}</div>}
        </div>
      </fieldset>

      {/* ─── Navigation ─── */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
        <button className="btn-secondary" data-testid="party-details-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn-primary" data-testid="party-details-next-btn" onClick={handleNext} disabled={emailCheck.isCheckingEmail || isProcessing}>
          {isProcessing ? 'Processing…' : emailCheck.isCheckingEmail ? 'Checking...' : 'Next →'}
        </button>
      </div>

      {/* ─── Session Recovery Modal ─── */}
      {emailCheck.showSessionModal && emailCheck.foundSession && (
        <SessionRecoveryModal
          email={partyEmail}
          businessName={(emailCheck.foundSession as any).businessDetails?.partyName}
          onRestore={emailCheck.handleRestoreSession}
          onStartFresh={emailCheck.handleStartFresh}
        />
      )}
    </div>
  );
};

export default PartyDetails;
